import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FindestSelectSearchPriority, FindestTextBox } from "Components";
import { SearchableDropdown } from "Components/Shared/SearchableDropdown";
import { SearchUnitControllerSingleton } from "Controllers";
import { MathematicalOperationEnum, SearchPriorityEnum, SearchSubTermTypeEnum, ToastTypeEnum } from "Enums";
import { ToastHelperSingleton } from "Helpers";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { TSearchUnitDTO, TSpecificationUnitDTO } from "Types";
import { UnitComparisonDropdown } from "./UnitComparisonDropdown";

import styles from "./savedQueryFields.module.scss";
import unitComparisonStyles from "./unitComparisonEditDropdown.module.scss";

type TNewSpecificationUnitProps = {
    specificationUnit: TSpecificationUnitDTO,
    changeSpecificationUnitSearchPriority: (id: number, newSpecificationUnitSearchPriority: SearchPriorityEnum) => void,
    changeUnitMathOperation: (id: number, newMathOperation: MathematicalOperationEnum) => void,
    deleteSpecificationUnit: (id: number) => void,
    editSearchSubTerm: (id: number, value: number | string, searchSubTermType: SearchSubTermTypeEnum) => Promise<boolean>,
    disabledPriority?: boolean
};

export const NewSpecificationUnit: FC<TNewSpecificationUnitProps> = ({ specificationUnit, disabledPriority,
    changeSpecificationUnitSearchPriority, deleteSpecificationUnit, changeUnitMathOperation, editSearchSubTerm }) => {

    const [unitValue, setUnitValue] = useState<number | undefined>(specificationUnit.unitValue);
    const [maximumUnitValue, setMaximumUnitValue] = useState<number | undefined>(specificationUnit.maxUnitValue);

    // Logic
    useEffect(() => {
        setUnitValue(specificationUnit.unitValue);
    }, [specificationUnit.unitValue]);

    useEffect(() => {
        setMaximumUnitValue(specificationUnit.maxUnitValue);
    }, [specificationUnit.maxUnitValue]);

    useEffect(() => {
        // when current specification is edited,
        // after 500 ms do:
        const delayDebounceFn = setTimeout(() => {
            // if new specification is null/empty/undefined, set new specification to the old one
            if (!unitValue) {
                setUnitValue(specificationUnit.unitValue);
                return;
            }
            // if the new specification is the same as the old one, do nothing
            if (unitValue === specificationUnit.unitValue) return;
            // set specification state
            setUnitValue(unitValue);
            // call parent on specification change
            editSearchSubTerm(specificationUnit.id, unitValue, SearchSubTermTypeEnum.UnitValue);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [editSearchSubTerm, unitValue, specificationUnit.id, specificationUnit.unitValue]);

    useEffect(() => {
        // when current specification is edited,
        // after 500 ms do:
        const delayDebounceFn = setTimeout(() => {
            // if new specification is null/empty/undefined, set new specification to the old one
            if (!maximumUnitValue) {
                setMaximumUnitValue(specificationUnit.maxUnitValue);
                return;
            }
            // if the new specification is the same as the old one, do nothing
            if (maximumUnitValue === specificationUnit.maxUnitValue) return;
            // set specification state
            setMaximumUnitValue(maximumUnitValue);
            // call parent on specification change
            editSearchSubTerm(specificationUnit.id, maximumUnitValue, SearchSubTermTypeEnum.MaximumUnitValue);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [editSearchSubTerm, maximumUnitValue, specificationUnit.id, specificationUnit.maxUnitValue]);

    // TODO: remove duplicated code
    const operationToPlaceholderText = () => {
        switch (specificationUnit.mathLimitType) {
            case MathematicalOperationEnum.GreaterThan:
                return "Greater than";      
            case MathematicalOperationEnum.LessThan:
                return "Less than";
            default:
                return "Greater than";
        }
    };

    const handleOnSpecificationUnitSearchPriorityOnChange = (newEnvironmentSearchPriority: SearchPriorityEnum) => {
        // set parent props keyword search priority
        changeSpecificationUnitSearchPriority(specificationUnit.id, newEnvironmentSearchPriority);
    };

    const handleMathOperationChange = async (newMathOperation: MathematicalOperationEnum) => {
        changeUnitMathOperation(specificationUnit.id, newMathOperation);
    };

    const handleOnCheckboxChange = (checked: boolean) => {
        changeSpecificationUnitSearchPriority(specificationUnit.id, checked ? SearchPriorityEnum.Should : SearchPriorityEnum.Disabled);
    };

    const handleUnitValueChange = (text: string) => {
        const unitValue = parseFloat(text);
        if (isNaN(unitValue)) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please input a valid number as unit value.");
        }

        setUnitValue(unitValue);
    };

    const handleMaximumUnitValueChange = (text: string) => {
        const unitValue = parseFloat(text);
        if (isNaN(unitValue)) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please input a valid number as unit value.");
        }

        setMaximumUnitValue(unitValue);
    };

    const onUnitClick = async (result: TSearchUnitDTO): Promise<boolean> => {
        const isSuccess = await editSearchSubTerm(specificationUnit.id, result.name, SearchSubTermTypeEnum.Unit);
        return isSuccess;
    };

    return (
        <div className={styles.specificationUnitContainer}>
            <div className={specificationUnit.searchType !== SearchPriorityEnum.Disabled && !disabledPriority ? styles.specificationUnit : [styles.specificationUnit, styles.disabled].join(" ")}>
                <input type="checkbox" checked={specificationUnit.searchType !== SearchPriorityEnum.Disabled} onChange={(event: ChangeEvent<HTMLInputElement>) => { handleOnCheckboxChange(event.target.checked); }} />
                <UnitComparisonDropdown overriddenStyles={unitComparisonStyles} mathematicalOperation={specificationUnit.mathLimitType} selectMathOperation={handleMathOperationChange} />

                {specificationUnit.mathLimitType !== MathematicalOperationEnum.None ?
                    specificationUnit.mathLimitType === MathematicalOperationEnum.Between ?
                        <div style={{display: "flex"}}>
                            <FindestTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder="Min" value={specificationUnit.unitValue?.toString()} onChange={handleUnitValueChange} />
                            <span className={styles.rangeDivider}>-</span>
                            <FindestTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder="Max" value={specificationUnit.maxUnitValue?.toString()} onChange={handleMaximumUnitValueChange} />
                        </div>
                        :
                        <div>
                            <FindestTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder={operationToPlaceholderText()} value={specificationUnit.unitValue?.toString()} onChange={handleUnitValueChange} />
                        </div>
                    : null}
                <SearchableDropdown<TSearchUnitDTO> placeholder="Unit" itemName="unit" asyncSearchFunction={SearchUnitControllerSingleton.searchAsync}
                    doClearValueOnClick={false} resultToIdAndName={SearchUnitControllerSingleton.searchUnitToIdAndName} onResultClickAsync={onUnitClick} 
                    value={specificationUnit.unit} overriddenStyles={styles} />
            </div>
            <div className={styles.searchPriorityContainer}>
                {specificationUnit.searchType !== SearchPriorityEnum.Disabled && !disabledPriority ?
                    <FindestSelectSearchPriority searchPriority={specificationUnit.searchType} onChange={handleOnSpecificationUnitSearchPriorityOnChange} />
                    : null}
                {specificationUnit.searchType !== SearchPriorityEnum.Disabled && !disabledPriority ?
                    <FontAwesomeIcon icon={faTrashAlt} onClick={() => { deleteSpecificationUnit(specificationUnit.id); }} />
                    : null}
            </div>
        </div>
    );
};